import * as React from "react";
import {useEffect, useRef, useState} from "react";
import "./styles.scss";

interface IProps {
    otp: string[];
    otpLength: number;
    otpType: string;
    updateOtp: (otpValue: string[]) => void;
    resendOtp?: () => void;
}

const ZERO_KEYCODE = 48;
const NINE_KEYCODE = 57;
const UPPER_A_KEYCODE = 65;
const LOWER_Z_KEYCODE = 122;
const FOCUS = {
    NEXT: "NEXT",
    BACK: "BACK",
    NUMBER: "NUMBER",
};
const input = (currentOtpIndex,
               otpValue,
               otpLength,
               otpType,
               focusIndex,
               handleOnKeyDown,
               handleOnChange,
               checkAndUpdateFocus) => {
    const inputRef = useRef(null);
    const inputStyle = {
        width: 38,
        height: 38,
        border: focusIndex === currentOtpIndex ? "2px solid #434175" : "1px solid #434175",
        borderRadius: 3,
        textAlign: "center" as const,
        color: "transparent",
        textShadow: "0px 0px 1px #000",
    };
    const inputType = otpType === "number" ? "tel" : "text";
    useEffect(() => {
        if (focusIndex === currentOtpIndex) {
            inputRef.current.focus();
        }
        if (focusIndex === -1 || focusIndex + 1 > otpLength) {
            inputRef.current.blur();
        }
    }, [focusIndex]);
    const handleFocus = (event) => {
        event.target.select();
    };
    return (
        <input
            id={currentOtpIndex}
            key={currentOtpIndex}
            style={inputStyle}
            type={inputType}
            value={otpValue}
            ref={inputRef}
            onFocus={(e) => handleFocus(e)}
            onKeyDown={(e) => handleOnKeyDown(e, currentOtpIndex)}
            onChange={(e) => handleOnChange(e, currentOtpIndex)}
            onClick={() => checkAndUpdateFocus(currentOtpIndex)}
        />
    );
};

const isValidateChar = (char, otpType) => {
    switch (otpType) {
        case "number":
            return !(
                char.charCodeAt(0) > NINE_KEYCODE || char.charCodeAt(0) < ZERO_KEYCODE
            );
        case "alpha":
            return !(
                char.charCodeAt(0) > LOWER_Z_KEYCODE ||
                char.charCodeAt(0) < UPPER_A_KEYCODE
            );
        case "alphanumeric":
            return !(
                char.charCodeAt(0) > LOWER_Z_KEYCODE ||
                char.charCodeAt(0) < ZERO_KEYCODE
            );
        default:
            return true;
    }
};

export const Otp = (props: IProps) => {
    const [focusIndex, updateFocus] = useState(0);
    const [resendTime, updateTime] = useState(60);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (resendTime > 0) {
                updateTime(resendTime - 1);
            } else {
                clearInterval(timer);
            }
        }, 1000);
    });
    useEffect(() => {
        if (props.otp.length === 0 && focusIndex >= 0) {
            updateFocus(-1);
        }
    }, [props.otp]);
    const checkAndUpdateFocus = (focus) => {
        if (focus === FOCUS.NEXT) {
            updateFocus(focusIndex + 1);
        } else if (focus === FOCUS.BACK) {
            if (focusIndex > -1) {
                updateFocus(focusIndex - 1);
            }
        } else if (focusIndex !== focus) {
            updateFocus(focus);
        }
    };

    const handleOnKeyDown = (e, otpIndex) => {
        const curOtp = props.otp;
        switch (e.key) {
            case "Backspace":
                e.preventDefault();
                curOtp[otpIndex] = "";
                props.updateOtp(curOtp);
                checkAndUpdateFocus(FOCUS.BACK);
                break;
            case "Delete":
                e.preventDefault();
                curOtp[otpIndex] = "";
                props.updateOtp(curOtp);
                break;
            case "ArrowLeft":
                e.preventDefault();
                checkAndUpdateFocus(FOCUS.BACK);
                break;
            case "ArrowRight":
                e.preventDefault();
                checkAndUpdateFocus(FOCUS.NEXT);
                break;
            default:
                if (isValidateChar(String(e.key), props.otpType) && props.otp[otpIndex] === String(e.key)) {
                    checkAndUpdateFocus(FOCUS.NEXT);
                }
                break;
        }
    };
    const handleOnChange = (e, otpIndex) => {
        const value = e.target.value.length > 1 ?
            String(e.target.value).replace(props.otp[otpIndex], "") : String(e.target.value);
        if (isValidateChar(value, props.otpType) && props.otp[otpIndex] !== value) {
            const curOtp = props.otp;
            curOtp[otpIndex] = value;
            props.updateOtp(curOtp);
            if (value.length === 0) {
                checkAndUpdateFocus(FOCUS.BACK);
            } else {
                checkAndUpdateFocus(FOCUS.NEXT);
            }
        }
    };
    return (
        <div className="otp">
            <div className="flex-center-row">
                {Array.from(Array(props.otpLength), (_, i) => i).map((currentOtpIndex) => {
                    const otpValue = props.otp.length >= currentOtpIndex + 1 ? props.otp[currentOtpIndex] : "";
                    const style = {
                        marginRight: currentOtpIndex + 1 === props.otpLength ? 0 : 12,
                    };
                    return (
                        <div key={currentOtpIndex} style={style}>
                            {input(currentOtpIndex,
                                otpValue,
                                props.otpLength,
                                props.otpType,
                                focusIndex,
                                handleOnKeyDown,
                                handleOnChange,
                                checkAndUpdateFocus)}
                        </div>
                    );
                })}
            </div>
            {props.resendOtp ?
                <div className="flex-center-row" style={{fontSize: 12, marginTop: 5}}>
                    Didn’t receive the OTP? {resendTime !== 0 ? ` RESEND OTP in ${resendTime} sec` :
                    <span className="resend-otp" onClick={() => {
                        props.updateOtp([]);
                        props.resendOtp();
                        updateTime(60);
                    }}>RESEND OTP</span>}
                </div> : null}
        </div>
    );
};

export default Otp;
